/**
 * Typable-date helpers.
 *
 * These back `src/mixins/typableDateMixin.js`, which lets the app's date fields
 * be typed into as well as picked from the calendar.
 *
 * The guiding idea: a wrong format is made IMPOSSIBLE rather than detected
 * afterwards. Only digits are accepted, separators are inserted automatically,
 * and a digit that cannot lead to a real date is refused at the keystroke. So
 * the field can only ever hold a well-formed prefix of a valid date, and the
 * only bad state left is "not finished yet".
 *
 * Everything is strict and single-format: a field is parsed only in the format
 * it already displays. We never guess across formats, because "03/04/2026" is
 * genuinely ambiguous and silently picking one reading would write wrong data.
 */

// Supported display formats. Each is a list of segments in display order.
// `key` is the calendar part, `width` the digit count, `sep` what follows it.
const SPECS = {
  "DD-MM-YYYY": [
    { key: "d", width: 2, sep: "-" },
    { key: "m", width: 2, sep: "-" },
    { key: "y", width: 4, sep: "" },
  ],
  "YYYY-MM-DD": [
    { key: "y", width: 4, sep: "-" },
    { key: "m", width: 2, sep: "-" },
    { key: "d", width: 2, sep: "" },
  ],
};

export const SUPPORTED_FORMATS = Object.keys(SPECS);

function spec(format) {
  const s = SPECS[format];
  if (!s) {
    // Deliberately loud: a new display format must be added here consciously,
    // never silently coerced into one of the existing ones.
    throw new Error(
      `[utils/date] Unsupported date format "${format}". Supported: ${SUPPORTED_FORMATS.join(", ")}`
    );
  }
  return s;
}

/** Total digits a complete date needs, e.g. 8. */
export function digitCount(format) {
  return spec(format).reduce((n, seg) => n + seg.width, 0);
}

/** Strip everything that isn't a digit. */
export function digitsOf(text) {
  return String(text == null ? "" : text).replace(/\D/g, "");
}

/**
 * Render a digit string into the display format, inserting separators as soon
 * as the segment before them is full. "1207" -> "12-07" for DD-MM-YYYY.
 */
export function formatDigits(digits, format) {
  const segs = spec(format);
  let out = "";
  let i = 0;
  for (const seg of segs) {
    if (i >= digits.length) break;
    const chunk = digits.slice(i, i + seg.width);
    out += chunk;
    i += seg.width;
    // Emit the separator as soon as the segment is FULL, without waiting for
    // the next digit -- so the user sees "12-" and never types punctuation.
    if (chunk.length === seg.width) out += seg.sep;
  }
  return out;
}

/** Split a digit string into whatever parts are known so far. */
export function partsOf(digits, format) {
  const segs = spec(format);
  const parts = {};
  let i = 0;
  for (const seg of segs) {
    parts[seg.key] = digits.slice(i, i + seg.width);
    i += seg.width;
  }
  return parts;
}

export function isComplete(digits, format) {
  return digits.length === digitCount(format);
}

const MAX_DAY = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isLeap(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** Days in a month; `year` may be unknown, in which case February allows 29. */
function daysInMonth(month, year) {
  if (month === 2) return year == null ? 29 : isLeap(year) ? 29 : 28;
  return MAX_DAY[month - 1];
}

/**
 * Could this partial digit string still become a real date?
 *
 * This is what makes wrong input unreachable. Checks are applied as soon as
 * enough digits exist to apply them, and no sooner -- so "3" is fine as a day
 * (it may become 30) but "32" is not, and "31" only becomes invalid once the
 * month turns out to be February.
 */
export function isPartialValid(digits, format) {
  const { d = "", m = "", y = "" } = partsOf(digits, format);

  // Day: 01-31, and no leading "00".
  if (d.length >= 1 && Number(d[0]) > 3) return false;
  if (d.length === 2) {
    const day = Number(d);
    if (day < 1 || day > 31) return false;
  }

  // Month: 01-12, no leading "00".
  if (m.length >= 1 && Number(m[0]) > 1) return false;
  if (m.length === 2) {
    const mon = Number(m);
    if (mon < 1 || mon > 12) return false;
  }

  // Year: keep it to plausible centuries so a typo can't produce year 0042.
  if (y.length >= 1 && !["1", "2"].includes(y[0])) return false;

  // Day-vs-month, once both are known. Year is folded in only when complete,
  // so 29-02 stays typable until the year proves it isn't a leap year.
  if (d.length === 2 && m.length === 2) {
    const day = Number(d);
    const mon = Number(m);
    const year = y.length === 4 ? Number(y) : null;
    if (day > daysInMonth(mon, year)) return false;
  }

  return true;
}

/** Complete digit string -> "YYYY-MM-DD", or null if not complete/real. */
export function toIso(digits, format) {
  if (!isComplete(digits, format)) return null;
  if (!isPartialValid(digits, format)) return null;
  const { d, m, y } = partsOf(digits, format);
  const iso = `${y}-${m}-${d}`;
  // Round-trip guard: new Date(2026, 1, 31) silently rolls into March, so
  // building the date is not enough -- it has to come back unchanged.
  const probe = new Date(`${iso}T00:00:00`);
  if (
    probe.getFullYear() !== Number(y) ||
    probe.getMonth() + 1 !== Number(m) ||
    probe.getDate() !== Number(d)
  ) {
    return null;
  }
  return iso;
}

/** "YYYY-MM-DD" -> digit string for the given display format. */
export function isoToDigits(iso, format) {
  if (!iso) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (!match) return "";
  const [, y, m, d] = match;
  return spec(format)
    .map((seg) => ({ y, m, d }[seg.key]))
    .join("");
}

/** "YYYY-MM-DD" -> display text, e.g. "12-07-2026". */
export function isoToDisplay(iso, format) {
  const digits = isoToDigits(iso, format);
  return digits ? formatDigits(digits, format) : "";
}

/**
 * Try to append one digit.
 * Returns the next digit string, or null if the digit can't lead anywhere real.
 */
export function acceptDigit(digits, digit, format) {
  if (!/^\d$/.test(digit)) return null;
  if (digits.length >= digitCount(format)) return null;
  const next = digits + digit;
  return isPartialValid(next, format) ? next : null;
}

/**
 * Bounds check for a completed date. Mirrors what the calendar enforces, so
 * typing can't reach a date the picker would have refused.
 */
export function withinBounds(iso, { min, max, allowedDates } = {}) {
  if (!iso) return false;
  if (min && iso < min) return false;
  if (max && iso > max) return false;
  if (typeof allowedDates === "function" && !allowedDates(iso)) return false;
  return true;
}

export const ERRORS = {
  INCOMPLETE: "Incomplete date",
  INVALID: "Invalid date",
  OUT_OF_RANGE: "Date not allowed",
};

/* ------------------------------------------------------------------ *
 * Ranges
 *
 * A range field is one text box holding two dates, e.g.
 * "12-07-2026 to 15-07-2026". Internally it is just one digit string:
 * the first `digitCount` digits are the start, the rest are the end.
 * Each half is validated with exactly the same rules as a single date.
 * ------------------------------------------------------------------ */

/**
 * Default separator between the two halves. Screens differ -- Payments renders
 * `2026-07-12 ~ 2026-07-15`, others `12-07-2026 to 15-07-2026` -- so each call
 * site passes its own and keeps the look it has today.
 */
export const RANGE_SEP = " to ";

export function formatRangeDigits(digits, format, sep = RANGE_SEP) {
  const n = digitCount(format);
  const from = digits.slice(0, n);
  const to = digits.slice(n);
  const left = formatDigits(from, format);
  // Show the separator as soon as the first date is done, so it's obvious a
  // second one is expected.
  if (from.length < n) return left;
  if (!to.length) return left + sep;
  return left + sep + formatDigits(to, format);
}

export function isRangeComplete(digits, format) {
  return digits.length === 2 * digitCount(format);
}

export function acceptRangeDigit(digits, digit, format) {
  const n = digitCount(format);
  if (digits.length < n) return acceptDigit(digits, digit, format);
  if (digits.length >= 2 * n) return null;
  const to = acceptDigit(digits.slice(n), digit, format);
  return to === null ? null : digits.slice(0, n) + to;
}

/** Complete range digits -> [fromIso, toIso], ascending. */
export function rangeToIso(digits, format) {
  if (!isRangeComplete(digits, format)) return null;
  const n = digitCount(format);
  const a = toIso(digits.slice(0, n), format);
  const b = toIso(digits.slice(n), format);
  if (!a || !b) return null;
  // Sort, mirroring what the screens' own formatDisplayDateRange does today.
  return a <= b ? [a, b] : [b, a];
}

/** [fromIso, toIso] -> digit string. */
export function rangeDigitsFromIso(value, format) {
  if (!Array.isArray(value) || !value.length) return "";
  const a = isoToDigits(value[0], format);
  const b = value.length > 1 ? isoToDigits(value[1], format) : "";
  return a + b;
}

export function rangeToDisplay(value, format, sep = RANGE_SEP) {
  const digits = rangeDigitsFromIso(value, format);
  return digits ? formatRangeDigits(digits, format, sep) : "";
}

export function validateRange(text, format, bounds = {}) {
  const digits = digitsOf(text);
  if (!digits) return { ok: false, iso: null, error: null };
  if (!isRangeComplete(digits, format)) {
    return { ok: false, iso: null, error: ERRORS.INCOMPLETE };
  }
  const pair = rangeToIso(digits, format);
  if (!pair) return { ok: false, iso: null, error: ERRORS.INVALID };
  if (!pair.every((d) => withinBounds(d, bounds))) {
    return { ok: false, iso: null, error: ERRORS.OUT_OF_RANGE };
  }
  return { ok: true, iso: pair, error: null };
}

/**
 * Full check for the current field text.
 * -> { ok, iso, error }  (`ok` false with no error means simply empty)
 */
export function validate(text, format, bounds = {}) {
  const digits = digitsOf(text);
  if (!digits) return { ok: false, iso: null, error: null };
  if (!isComplete(digits, format)) {
    return { ok: false, iso: null, error: ERRORS.INCOMPLETE };
  }
  const iso = toIso(digits, format);
  if (!iso) return { ok: false, iso: null, error: ERRORS.INVALID };
  if (!withinBounds(iso, bounds)) {
    return { ok: false, iso: null, error: ERRORS.OUT_OF_RANGE };
  }
  return { ok: true, iso, error: null };
}

/* ------------------------------------------------------------------ *
 * Local calendar dates
 *
 * Never derive "today" from toISOString(): that is UTC, and in India it still
 * reads as yesterday until 05:30, so `:max="today"` greys out today's date
 * every night after midnight.
 * ------------------------------------------------------------------ */

/** Date -> "YYYY-MM-DD" on the reader's own calendar. */
export function toLocalIsoDate(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Today as "YYYY-MM-DD" on the reader's own calendar. */
export function localToday() {
  return toLocalIsoDate(new Date());
}
